import { useEffect, useId, useState, type FormEvent } from 'react';
import { formatDate, formatEuro, formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Card, Dialog, SearchField, Skeleton, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { SettingsPageHeader } from '../../settings/SettingsFrame';
import { cancelDeal, useCancellableDeals, useInvalidateDeals } from '../api';
import { FieldBlock } from '../dialogs/bits';
import { NextStepFields, useNextStepForm } from '../dialogs/NextStepFields';
import type { CancellableDeal, DealRecord } from '../types';
import dialogStyles from '../dialogs/dialogs.module.css';
import styles from './settings.module.css';
import frameStyles from '../../settings/SettingsFrame.module.css';

/**
 * Einstellungen · Deals (Storno, P-10): the intro of Deal-Pipeline 16, the search of the cancellable deals
 * (`GET /deals/cancellable`) and „Storno setzen" (reason, credit-note remark, next step → `POST /deals/:id/cancel`).
 * There is no designed frame; the page follows the settings pattern.
 */

const CONSEQUENCES = [
  'Der Deal verschwindet sofort für Setter und Closer; sichtbar bleibt er für Admin und Backoffice.',
  'Die Einheit wird frei, die Reservierung aufgehoben.',
  'Die Provisionspositionen werden als rückforderbar markiert, die Rechnung wird gutgeschrieben.',
  'Offene Tasks werden geschlossen; der Kontakt geht auf Follow Up mit dem nächsten Schritt.',
];

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function CancelDealDialog({
  deal,
  onClose,
  onDone,
}: {
  deal: CancellableDeal | null;
  onClose: () => void;
  onDone: (record: DealRecord) => void;
}) {
  const formId = useId();
  const [reason, setReason] = useState('');
  const [remark, setRemark] = useState('');
  const [errors, setErrors] = useState<{ reason?: string | undefined; nextStep?: string | undefined }>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const nextStep = useNextStepForm('follow_up');
  const open = deal !== null;

  useEffect(() => {
    if (!open) return;
    setReason('');
    setRemark('');
    setErrors({});
    setBanner(null);
    nextStep.reset({ kind: 'follow_up' });
  }, [open, deal?.id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!deal || pending) return;
    const next: typeof errors = {};
    if (reason.trim() === '') next.reason = 'Bitte geben Sie einen Grund an.';
    const step = nextStep.build();
    if (step === null) next.nextStep = 'Bitte legen Sie den nächsten Schritt fest.';
    setErrors(next);
    if (Object.keys(next).length > 0 || step === null) return;
    setPending(true);
    setBanner(null);
    try {
      onDone(
        await cancelDeal(deal.id, {
          reason: reason.trim(),
          creditNoteRemark: remark.trim() === '' ? null : remark.trim(),
          nextStep: step,
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0) {
        setErrors({ reason: error.fields['reason'], nextStep: error.fields['nextStep'] });
      } else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone="danger"
      title="Storno setzen"
      subtitle={
        deal
          ? `${deal.contactName} · ${deal.unitLine}${deal.priceCents === null ? '' : ` · ${formatEuro(deal.priceCents)}`}`
          : ''
      }
      className={dialogStyles.stageDialog}
      initialFocus="container"
      footerNote="Storno ist absichtlich unbequem: rechtepflichtig und mit ausgewiesenen Folgen."
      footer={
        <>
          <Button variant="secondary" className={dialogStyles.footBtn} onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="destructiveFilled"
            className={dialogStyles.footBtn}
            loading={pending}
          >
            Storno setzen
          </Button>
        </>
      }
    >
      <form id={formId} className={dialogStyles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {deal ? (
          <div className={dialogStyles.tiles}>
            <div className={dialogStyles.tile}>
              <div className={dialogStyles.sectionLabel}>Provision</div>
              <div className={dialogStyles.tileValue}>{formatEuro(deal.commissionCents)}</div>
            </div>
            <div className={dialogStyles.tile}>
              <div className={dialogStyles.sectionLabel}>Rechnung</div>
              <div className={dialogStyles.tileText}>{deal.invoiceNumber ?? '—'}</div>
            </div>
            <div className={dialogStyles.tile}>
              <div className={dialogStyles.sectionLabel}>Eingang</div>
              <div className={dialogStyles.tileText}>{formatDate(deal.receivedAt)}</div>
            </div>
          </div>
        ) : null}
        <div>
          <div className={dialogStyles.sectionLabel}>Was Storno auslöst</div>
          <div className={dialogStyles.consequences} style={{ marginTop: 8 }}>
            {CONSEQUENCES.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </div>
        <FieldBlock label="Grund" note="Pflichtangabe" error={errors.reason} htmlFor={`${formId}-reason`}>
          <textarea
            id={`${formId}-reason`}
            className={clsx(
              dialogStyles.input,
              dialogStyles.textarea,
              errors.reason && dialogStyles.inputInvalid,
            )}
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </FieldBlock>
        <FieldBlock
          label="Vermerk auf der Gutschrift"
          note="optional · Standard „Gutschrift nach Storno“"
          htmlFor={`${formId}-remark`}
        >
          <input
            id={`${formId}-remark`}
            className={dialogStyles.input}
            value={remark}
            onChange={(event) => setRemark(event.target.value)}
          />
        </FieldBlock>
        <NextStepFields form={nextStep} error={errors.nextStep} kinds={['follow_up', 'task']} />
      </form>
    </Dialog>
  );
}

export function CancellationSettingsPage() {
  const { toast } = useToast();
  const invalidate = useInvalidateDeals();
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);
  const deals = useCancellableDeals(q, true);
  const [selected, setSelected] = useState<CancellableDeal | null>(null);
  const forbidden = deals.error instanceof ApiError && deals.error.status === 403;

  return (
    <div className={frameStyles.page}>
      <SettingsPageHeader
        eyebrow="Organisation"
        title="Deals"
        description="Storno eines abgerechneten Deals. Vorher gilt „Verloren setzen“ in der Pipeline."
      />
      <div className={styles.intro} role="status">
        Storno steht in den Einstellungen, nicht in der Pipeline. Ein stornierter Deal verschwindet sofort für
        Setter und Closer. Sichtbar bleibt er für Admin und Backoffice — die Einheit ist frei, die
        Provisionspositionen sind als rückforderbar markiert. Setzen kann Storno nur ein Admin, über
        Einstellungen · Deals.
      </div>
      <div style={{ maxWidth: 420 }}>
        <SearchField
          label="Deal suchen"
          labelVisible
          placeholder="Name, Einheit, Deal- oder Rechnungsnummer"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {deals.isPending ? (
        <Card className={styles.results}>
          <Skeleton variant="block" height={52} />
        </Card>
      ) : null}
      {deals.isError ? (
        forbidden ? (
          <Banner tone="info">{deals.error.message}</Banner>
        ) : (
          <Banner tone="danger">{errorMessage(deals.error)}</Banner>
        )
      ) : null}
      {deals.data ? (
        <Card className={styles.results} aria-label="Stornierbare Deals">
          <div className={clsx(styles.resultRow, styles.rowHead)}>
            <span>Deal</span>
            <span>Einheit</span>
            <span>Kaufpreis</span>
            <span>Provision</span>
            <span>Rechnung</span>
            <span />
          </div>
          {deals.data.items.length === 0 ? (
            <div className={styles.empty}>
              {q
                ? `Kein abgerechneter Deal zu „${q}“.`
                : 'Kein Deal mit Provisionseingang — Storno ist erst danach möglich.'}
            </div>
          ) : null}
          {deals.data.items.map((deal) => (
            <div key={deal.id} className={styles.resultRow}>
              <span>
                <div className={styles.resultName}>{deal.contactName}</div>
                <div className={styles.resultMeta}>
                  Deal #{formatInteger(deal.number)}
                  {deal.closerShort ? ` · Closer ${deal.closerShort}` : ''}
                </div>
              </span>
              <span className={styles.rowValue}>{deal.unitLine}</span>
              <span className={styles.rowValue}>
                {deal.priceCents === null ? '—' : formatEuro(deal.priceCents)}
              </span>
              <span className={styles.rowValue}>{formatEuro(deal.commissionCents)}</span>
              <span className={styles.rowState}>
                {deal.invoiceNumber ?? '—'} · eingegangen {formatDate(deal.receivedAt)}
              </span>
              <span>
                <Button variant="destructive" size="small" onClick={() => setSelected(deal)}>
                  Storno setzen
                </Button>
              </span>
            </div>
          ))}
        </Card>
      ) : null}
      <CancelDealDialog
        deal={selected}
        onClose={() => setSelected(null)}
        onDone={(record) => {
          setSelected(null);
          void invalidate(record.id);
          toast({ message: `Storno gesetzt: ${record.contact.name}.` });
        }}
      />
    </div>
  );
}
